import { Head, Link, router, usePoll } from '@inertiajs/react'
import {
  AudioLines,
  CircleCheck,
  Download,
  FilePlus2,
  LoaderCircle,
  Mic2,
  Pencil,
  Play,
  Plus,
  Send,
  Square,
  Trash2,
  TriangleAlert,
} from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { CharacterChip } from '@/components/character-chip'
import { PlaybackBar } from '@/components/playback-bar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePlayerShortcuts } from '@/hooks/use-player-shortcuts'
import { useScenePlayer } from '@/hooks/use-scene-player'
import {
  adminCharacters,
  adminProductionArchives,
  adminPublications,
  adminScenes,
  adminScriptImports,
  adminSpeech,
  adminVoiceAuditions,
} from '@/routes'
import type { CastMember, Character, Production, SceneSummary, Voice } from '@/types'

type Props = {
  production: Production
  characters: CastMember[]
  scenes: SceneSummary[]
  voices: Voice[]
  /** Why voices couldn't be listed (no key, key lacks voices_read, ElevenLabs unreachable). */
  voicesError: string | null
  /** AI takes still generating or processing; the page polls while > 0. */
  generatingCount: number
  ttsConfigured: boolean
}

export default function ShowProduction({
  production,
  characters,
  scenes,
  voices,
  voicesError,
  ttsConfigured,
  generatingCount,
}: Props) {
  const byId = new Map(characters.map((c) => [c.id, c]))
  const player = useScenePlayer(production.slug, { defaultMode: 'continuous' })
  usePlayerShortcuts(player)

  // While voices generate in the background, refresh the counts and badges.
  const poll = usePoll(3000, { only: ['scenes', 'characters', 'generatingCount'] }, { autoStart: false })
  useEffect(() => {
    generatingCount > 0 ? poll.start() : poll.stop()
  }, [generatingCount, poll])
  const voiceNames = new Map(voices.map((v) => [v.id, v.name.split(' - ')[0]]))
  const unvoiced = characters.filter((c) => !c.voiceId && c.lineCount > 0).length

  // The open tab lives in the URL hash (#cast), so links and Back return to it.
  const [tab, setTab] = useState(() => (window.location.hash === '#cast' ? 'cast' : 'scenes'))
  const showTab = (value: string) => {
    setTab(value)
    // Keep Inertia's page in history.state; only the hash changes.
    window.history.replaceState(window.history.state, '', value === 'cast' ? '#cast' : window.location.pathname)
  }
  const slug = { productionId: production.slug }
  const spoken = scenes.reduce((sum, s) => sum + s.spokenCount, 0)
  const voiced = scenes.reduce((sum, s) => sum + s.voicedCount, 0)

  return (
    <div className="grid gap-6">
      <Head title={production.title} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{production.title}</h1>
          <p className="text-muted-foreground">
            {production.publishedVersion > 0 ? `Published v${production.publishedVersion}` : 'Not yet published'} ·{' '}
            {scenes.length} {scenes.length === 1 ? 'scene' : 'scenes'} · {voiced}/{spoken} lines voiced
            {generatingCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-foreground">
                <LoaderCircle className="size-3.5 animate-spin" /> generating {generatingCount}…
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" asChild>
            {/* A file download, not an Inertia visit. */}
            <a href={adminProductionArchives.show(slug).url} title="Script, voices and audio as a .scripted file">
              <Download /> Export
            </a>
          </Button>
          <Button variant="outline" asChild>
            <Link href={adminScenes.new(slug)}>
              <Plus /> Add scene
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link href={adminScriptImports.new(slug)}>
              <FilePlus2 /> Import script
            </Link>
          </Button>
          <Button variant="outline" disabled={!ttsConfigured} onClick={() => router.visit(adminSpeech.create(slug))}>
            <AudioLines /> Generate voices
          </Button>
          <Button disabled={scenes.length === 0} onClick={() => router.visit(adminPublications.create(slug))}>
            <Send /> Publish to cast
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={showTab}>
        <TabsList>
          <TabsTrigger value="scenes">Scenes ({scenes.length})</TabsTrigger>
          <TabsTrigger value="cast">
            Cast ({characters.length})
            {unvoiced > 0 && (
              <span className="size-1.5 rounded-full bg-amber-500" title={`${unvoiced} without a voice`} />
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="scenes">
          <Card>
            <CardHeader>
              <CardDescription>Open a scene to read, play and fix its lines.</CardDescription>
            </CardHeader>
            <CardContent>
              {scenes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No scenes yet — import a script or add a scene.</p>
              ) : (
                <SceneTable scenes={scenes} characters={byId} productionId={production.slug} player={player} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cast">
          <Card>
            <CardHeader>
              {voicesError ? (
                <Alert variant="destructive">
                  <TriangleAlert />
                  <AlertDescription>{voicesError}</AlertDescription>
                </Alert>
              ) : (
                <CardDescription>Biggest parts first. Audition voices on each part’s own lines.</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {characters.length === 0 ? (
                <p className="text-sm text-muted-foreground">Import a script to find the cast.</p>
              ) : (
                <CastTable
                  characters={characters}
                  voiceNames={voiceNames}
                  productionId={production.slug}
                  onRemove={(c) => {
                    if (!window.confirm(`Remove ${c.name} from the cast?`)) return
                    // Stay on the Cast tab: keep this page's state and put #cast back after the redirect.
                    router.visit(adminCharacters.destroy({ productionId: production.slug, id: c.id }), {
                      preserveState: true,
                      preserveScroll: true,
                      onSuccess: () => showTab('cast'),
                    })
                  }}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PlaybackBar player={player} productionId={production.slug} scenes={scenes} />
    </div>
  )
}

function SceneTable({
  scenes,
  characters,
  productionId,
  player,
}: {
  scenes: SceneSummary[]
  characters: Map<number, Character>
  productionId: string
  player: ReturnType<typeof useScenePlayer>
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Scene</TableHead>
          <TableHead className="hidden md:table-cell">Cast</TableHead>
          <TableHead className="w-36">Audio</TableHead>
          <TableHead className="w-0" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {scenes.map((scene, i) => {
          const showAct = scene.act && scene.act !== scenes[i - 1]?.act
          const playing = player.sceneId === scene.id
          const href = adminScenes.show({ productionId, id: scene.id })
          return (
            <Fragment key={scene.id}>
              {showAct && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={4} className="pt-4 text-xs font-semibold uppercase text-muted-foreground">
                    {scene.act}
                  </TableCell>
                </TableRow>
              )}
              <TableRow className="cursor-pointer" onClick={() => router.visit(href)}>
                <TableCell className="whitespace-normal">
                  <Link href={href} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
                    {scene.title}
                  </Link>
                  <p className="text-xs text-muted-foreground">{scene.lineCount} lines</p>
                </TableCell>
                <TableCell className="hidden whitespace-normal md:table-cell">
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {scene.characterIds.flatMap((id) => {
                      const c = characters.get(id)
                      return c ? [<CharacterChip key={id} character={c} />] : []
                    })}
                  </div>
                </TableCell>
                <TableCell>
                  <AudioProgress
                    voiced={scene.voicedCount}
                    spoken={scene.spokenCount}
                    generating={scene.generatingCount}
                    attention={scene.attentionCount}
                  />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={playing ? 'Stop' : 'Play scene'}
                      title={playing ? 'Stop' : 'Play scene'}
                      disabled={player.status === 'loading' || scene.lineCount === 0}
                      onClick={() => (playing ? player.stop() : void player.play(scene.id))}
                    >
                      {playing ? <Square /> : <Play />}
                    </Button>
                    <Button variant="ghost" size="icon" aria-label="Edit text" title="Edit text" asChild>
                      <Link href={adminScenes.edit({ productionId, id: scene.id })}>
                        <Pencil />
                      </Link>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            </Fragment>
          )
        })}
      </TableBody>
    </Table>
  )
}

/** Voiced/spoken with a bar, a tick when complete, and generating/attention markers. */
function AudioProgress({
  voiced,
  spoken,
  generating = 0,
  attention = 0,
}: {
  voiced: number
  spoken: number
  generating?: number
  attention?: number
}) {
  if (spoken === 0) return <span className="text-xs text-muted-foreground">—</span>
  const done = voiced === spoken
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-1.5 text-xs">
        {done ? (
          <CircleCheck className="size-3.5 text-emerald-600" />
        ) : (
          <span className="tabular-nums">
            {voiced}/{spoken}
          </span>
        )}
        {generating > 0 && <LoaderCircle className="size-3.5 animate-spin text-muted-foreground" />}
        {attention > 0 && (
          <Tooltip>
            <TooltipTrigger className="inline-flex items-center gap-0.5 text-amber-600">
              <TriangleAlert className="size-3.5" /> {attention}
            </TooltipTrigger>
            <TooltipContent>
              {attention === 1 ? 'A line' : `${attention} lines`} reworded since the audio or “spoken as” was made
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      {!done && <Progress value={(voiced / spoken) * 100} className="h-1" />}
    </div>
  )
}

function CastTable({
  characters,
  voiceNames,
  productionId,
  onRemove,
}: {
  characters: CastMember[]
  voiceNames: Map<string, string>
  productionId: string
  onRemove: (character: CastMember) => void
}) {
  const sorted = [...characters].sort((a, b) => b.lineCount - a.lineCount)
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Character</TableHead>
          <TableHead className="hidden md:table-cell">Played by</TableHead>
          <TableHead className="w-28">Lines</TableHead>
          <TableHead className="w-36">Audio</TableHead>
          <TableHead className="w-0">Voice</TableHead>
          <TableHead className="w-0" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((c) => (
          <TableRow key={c.id}>
            <TableCell className="whitespace-normal">
              <CharacterChip character={c} />
              {c.description && <p className="text-xs text-muted-foreground">{c.description}</p>}
            </TableCell>
            <TableCell className="hidden whitespace-normal text-sm md:table-cell">
              {c.actors.length > 0 ? c.actors.join(', ') : <span className="text-muted-foreground">—</span>}
            </TableCell>
            <TableCell className="text-xs">
              <span className="tabular-nums">{c.lineCount}</span>
              <span className="text-muted-foreground">
                {' '}
                · {c.sceneCount} {c.sceneCount === 1 ? 'scene' : 'scenes'}
              </span>
            </TableCell>
            <TableCell>
              <AudioProgress voiced={c.voicedCount} spoken={c.lineCount} />
            </TableCell>
            <TableCell>
              <Button
                variant={c.voiceId || c.lineCount === 0 ? 'outline' : 'secondary'}
                size="sm"
                className="w-44 justify-start"
                asChild
              >
                <Link href={adminVoiceAuditions.show({ productionId, characterId: c.id })}>
                  <Mic2 className={c.voiceId ? undefined : 'text-amber-600'} />
                  <span className="truncate">
                    {c.voiceId ? (voiceNames.get(c.voiceId) ?? 'Custom voice') : 'Choose a voice…'}
                  </span>
                </Link>
              </Button>
            </TableCell>
            <TableCell>
              {c.lineCount === 0 ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${c.name}`}
                  title="Remove from the cast"
                  onClick={() => onRemove(c)}
                >
                  <Trash2 />
                </Button>
              ) : (
                <Tooltip>
                  {/* aria-disabled, not disabled: a disabled button gets no hover, so no tooltip. */}
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-disabled
                      aria-label={`Remove ${c.name}`}
                      className="cursor-not-allowed opacity-50 hover:bg-transparent"
                    >
                      <Trash2 />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Still speaks {c.lineCount} {c.lineCount === 1 ? 'line' : 'lines'} — change them in the script to
                    remove
                  </TooltipContent>
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
